import { feedbackStore, TAGLINE_OPTIONS } from '../data/feedback-store.js';

/**
 * Score picker component helper (1-5 scale)
 */
function renderScorePicker({ name, label, helper, currentVal, conceptId, rubricKey }) {
  const scores = [1, 2, 3, 4, 5];
  const scoreLabels = {
    1: '1 - Low / Weak',
    2: '2 - Modest',
    3: '3 - Moderate',
    4: '4 - Strong',
    5: '5 - Exceptional'
  };

  return `
    <div class="score-picker-group">
      <div style="display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 4px;">
        <label class="score-label">${label}</label>
        <span class="mono" style="font-size: 11px; color: ${currentVal ? 'var(--accent-primary)' : 'var(--text-tertiary)'}; font-weight: 700;">
          ${currentVal ? `${currentVal}/5 (${scoreLabels[currentVal].split(' - ')[1]})` : 'Unrated'}
        </span>
      </div>
      <p class="score-helper">${helper}</p>
      <div class="score-buttons-row">
        ${scores.map(s => `
          <button 
            type="button" 
            class="score-btn ${currentVal === s ? 'active' : ''}" 
            data-score-btn="${s}"
            data-concept-id="${conceptId}"
            data-rubric-key="${rubricKey}"
            title="${scoreLabels[s]}"
          >
            ${s}
          </button>
        `).join('')}
      </div>
    </div>
  `;
}

/**
 * Inline Feedback Widget for each of the 3 Concept Views
 */
export function renderConceptFeedbackWidget(concept) {
  const state = feedbackStore.getState();
  const cf = state.conceptsFeedback[concept.id] || {};
  const activeSite = state.profile.site || 'Your Campus';

  return `
    <div class="inline-feedback-card" id="feedback-section-${concept.id}">
      <div class="inline-feedback-header">
        <div style="display: flex; align-items: center; gap: var(--space-sm);">
          <span style="font-size: 24px;">📝</span>
          <div>
            <h3 style="margin: 0; font-size: var(--text-xl); color: var(--text-primary);">
              Evaluate Concept 0${concept.number}: ${concept.name}
            </h3>
            <p style="margin: 0; font-size: var(--text-xs); color: var(--text-secondary);">
              Your feedback directly shapes the final brand recommendation presented to UVC leadership.
            </p>
          </div>
        </div>
        <span class="badge" style="background: rgba(99, 102, 241, 0.15); color: #818cf8; border: 1px solid var(--border-accent);">
          Auto-Saved Locally
        </span>
      </div>

      <!-- 4-Dimension Rubric Scoring -->
      <div class="rubric-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: var(--space-lg); margin-bottom: var(--space-xl);">
        ${renderScorePicker({
          name: 'boldScore',
          label: '⚡ 1. Boldness & Justice',
          helper: 'Does this project courageous truth-telling, prophetic justice, and tearing down walls?',
          currentVal: cf.boldScore,
          conceptId: concept.id,
          rubricKey: 'boldScore'
        })}

        ${renderScorePicker({
          name: 'inclusiveScore',
          label: '🌈 2. Inclusivity & Welcome',
          helper: 'Does this radiate radical LGBTQIA+ welcome, racial equity, and unconditional hospitality?',
          currentVal: cf.inclusiveScore,
          conceptId: concept.id,
          rubricKey: 'inclusiveScore'
        })}

        ${renderScorePicker({
          name: 'relevantScore',
          label: '🏙️ 3. Cultural Relevance',
          helper: 'Is this compelling for deconstructing seekers and authentic to Chicago urban life?',
          currentVal: cf.relevantScore,
          conceptId: concept.id,
          rubricKey: 'relevantScore'
        })}

        ${renderScorePicker({
          name: 'siteFitScore',
          label: `📍 4. Fit for ${activeSite}`,
          helper: `How naturally does this aesthetic and tone resonate in your specific ministry context?`,
          currentVal: cf.siteFitScore,
          conceptId: concept.id,
          rubricKey: 'siteFitScore'
        })}
      </div>

      <!-- 2 Qualitative Prompts -->
      <div class="grid-2" style="gap: var(--space-lg); margin-bottom: var(--space-xl);">
        <div>
          <label style="display: block; font-size: var(--text-xs); font-weight: 700; color: var(--text-primary); text-transform: uppercase; margin-bottom: 6px;">
            ✨ What resonates most or feels most inspiring about this direction?
          </label>
          <textarea 
            class="form-textarea" 
            data-concept-text="whatResonates" 
            data-concept-id="${concept.id}" 
            placeholder="e.g. The wheatpaste aesthetic feels unapologetic and matches our street presence..."
            rows="3"
          >${cf.whatResonates || ''}</textarea>
        </div>

        <div>
          <label style="display: block; font-size: var(--text-xs); font-weight: 700; color: var(--text-primary); text-transform: uppercase; margin-bottom: 6px;">
            ⚠️ What concerns, risks, or questions do you have for your campus?
          </label>
          <textarea 
            class="form-textarea" 
            data-concept-text="concernsOrQuestions" 
            data-concept-id="${concept.id}" 
            placeholder="e.g. Wondering if the dark color scheme feels too intense for families on Sunday mornings..."
            rows="3"
          >${cf.concernsOrQuestions || ''}</textarea>
        </div>
      </div>

      <!-- Navigation & Action Row -->
      <div style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid var(--border-subtle); padding-top: var(--space-md); flex-wrap: wrap; gap: var(--space-sm);">
        <div style="font-size: var(--text-xs); color: var(--text-tertiary); display: flex; align-items: center; gap: 6px;">
          <span style="color: #00C853;">✓</span> Responses saved to device
        </div>
        
        <div style="display: flex; gap: var(--space-sm);">
          ${concept.id === 'concept-1' ? `
            <button class="btn btn-secondary" data-concept="concept-2" style="font-size: var(--text-xs);">
              Next: Review Concept 02 &rarr;
            </button>
          ` : concept.id === 'concept-2' ? `
            <button class="btn btn-secondary" data-concept="concept-3" style="font-size: var(--text-xs);">
              Next: Review Concept 03 &rarr;
            </button>
          ` : `
            <button class="btn btn-secondary" data-tab="comparison" style="font-size: var(--text-xs);">
              Next: View Comparison Matrix &rarr;
            </button>
          `}
          <button class="btn btn-primary" data-tab="ballot" style="background: var(--accent-primary); color: #FFFFFF; font-size: var(--text-xs); padding: 6px 14px;">
            🗳️ Go to Final Ballot
          </button>
        </div>
      </div>
    </div>
  `;
}

/**
 * Strategy View Inline Feedback Widget
 */
export function renderStrategyFeedbackWidget() {
  const state = feedbackStore.getState();
  const sf = state.strategyFeedback;

  return `
    <div class="inline-feedback-card" id="feedback-section-strategy" style="margin-top: var(--space-2xl);">
      <div class="inline-feedback-header">
        <div style="display: flex; align-items: center; gap: var(--space-sm);">
          <span style="font-size: 24px;">🧭</span>
          <div>
            <h3 style="margin: 0; font-size: var(--text-xl); color: var(--text-primary);">
              Strategy &amp; Tagline Feedback
            </h3>
            <p style="margin: 0; font-size: var(--text-xs); color: var(--text-secondary);">
              Help refine UVC's core positioning, tagline candidates, and public voice.
            </p>
          </div>
        </div>
        <span class="badge" style="background: rgba(99, 102, 241, 0.15); color: #818cf8; border: 1px solid var(--border-accent);">
          Strategic Input
        </span>
      </div>

      <!-- Tagline Options Grid -->
      <div style="margin-bottom: var(--space-xl);">
        <label style="display: block; font-size: var(--text-xs); font-weight: 700; color: var(--text-primary); text-transform: uppercase; margin-bottom: 8px;">
          Select Preferred Tagline Contender
        </label>
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: var(--space-sm);">
          ${TAGLINE_OPTIONS.map(opt => `
            <label class="tagline-option-card ${sf.taglineChoice === opt.id ? 'active' : ''}">
              <input type="radio" name="taglineChoice" value="${opt.id}" ${sf.taglineChoice === opt.id ? 'checked' : ''} style="display: none;" />
              <div style="font-size: var(--text-sm); font-weight: 700; color: var(--text-primary); margin-bottom: 2px;">
                "${opt.text}"
              </div>
              <div style="font-size: 11px; color: var(--text-tertiary);">${opt.source}</div>
            </label>
          `).join('')}
        </div>
      </div>

      <!-- Custom Tagline & Mission Notes -->
      <div class="grid-2" style="gap: var(--space-lg); margin-bottom: var(--space-xl);">
        <div>
          <label style="display: block; font-size: var(--text-xs); font-weight: 700; color: var(--text-primary); text-transform: uppercase; margin-bottom: 6px;">
            Write an Alternative Tagline or Slogan Idea (Optional)
          </label>
          <input 
            type="text" 
            class="form-input" 
            id="strat-custom-tagline" 
            placeholder="e.g. Faith for the Real World. Welcome for All." 
            value="${sf.customTagline || ''}" 
          />
        </div>

        <div>
          <label style="display: block; font-size: var(--text-xs); font-weight: 700; color: var(--text-primary); text-transform: uppercase; margin-bottom: 6px;">
            Mission &amp; "Sanctuary &amp; Catalyst" Positioning Thoughts
          </label>
          <textarea 
            class="form-textarea" 
            id="strat-comments" 
            placeholder="Feedback on the mission statement, Say This/Not That playbook, or elevator pitches..."
            rows="2"
          >${sf.comments || ''}</textarea>
        </div>
      </div>

      <div style="display: flex; justify-content: flex-end; gap: var(--space-sm); border-top: 1px solid var(--border-subtle); padding-top: var(--space-md);">
        <button class="btn btn-primary" data-tab="concepts" style="background: var(--accent-primary); color: #FFFFFF; font-size: var(--text-xs); padding: 8px 18px;">
          Proceed to Explore the 3 Brand Concepts &rarr;
        </button>
      </div>
    </div>
  `;
}

/**
 * Micro-reaction buttons generator for collateral mockups
 */
export function renderMicroReactions(conceptId, itemId) {
  const state = feedbackStore.getState();
  const reactions = state.conceptsFeedback[conceptId]?.microReactions || {};
  const currentReaction = reactions[itemId];

  const reactionOptions = [
    { emoji: '🔥', label: 'Bold' },
    { emoji: '❤️', label: 'Love' },
    { emoji: '✨', label: 'Fresh' },
    { emoji: '💡', label: 'Smart' },
    { emoji: '🤔', label: 'Tweak' }
  ];

  return `
    <div class="micro-reactions-bar">
      <span style="font-size: 10px; color: var(--text-tertiary); font-weight: 600; text-transform: uppercase;">Reaction:</span>
      <div style="display: flex; gap: 4px;">
        ${reactionOptions.map(r => `
          <button 
            type="button" 
            class="micro-reaction-btn ${currentReaction === r.emoji ? 'active' : ''}" 
            data-micro-reaction="${r.emoji}" 
            data-concept-id="${conceptId}" 
            data-item-id="${itemId}"
            title="${r.label}"
          >
            ${r.emoji}
          </button>
        `).join('')}
      </div>
    </div>
  `;
}

/**
 * Persistent Review Tracker Footer / Banner
 */
export function renderReviewTrackerBanner(currentTab) {
  const stats = feedbackStore.getProgressStats();

  return `
    <aside class="review-tracker-banner" id="review-tracker-banner">
      <div style="display: flex; align-items: center; gap: var(--space-md); flex-wrap: wrap;">
        <div style="display: flex; align-items: center; gap: 8px;">
          <span style="font-size: 16px;">📋</span>
          <div>
            <span style="font-size: var(--text-xs); font-weight: 700; color: var(--text-primary);">
              Review Progress: ${stats.completedCount}/${stats.totalCount} Sections
            </span>
            <div class="progress-bar-track" style="width: 120px; height: 6px; background: rgba(255,255,255,0.1); border-radius: 3px; overflow: hidden; margin-top: 3px;">
              <div class="progress-bar-fill" style="width: ${stats.percentage}%; height: 100%; background: var(--accent-primary); transition: width 0.3s ease;"></div>
            </div>
          </div>
        </div>

        <div style="font-size: 11px; color: var(--text-secondary); display: none; @media(min-width: 600px){display: block;}">
          ${stats.isProfileComplete ? '✓ Profile Set' : '⚠️ Profile Needed'} • ${stats.isBallotComplete ? '✓ Ballot Ranked' : 'Ballot Pending'}
        </div>
      </div>

      <div style="display: flex; gap: var(--space-sm); align-items: center;">
        ${!stats.isProfileComplete ? `
          <button class="btn btn-secondary btn-sm" id="btn-banner-set-profile" style="font-size: 11px; border-color: #FFB300; color: #FFB300;">
            👤 Set Profile
          </button>
        ` : ''}
        <button class="btn btn-primary btn-sm" data-tab="ballot" style="background: var(--accent-primary); color: #FFFFFF; font-size: 11px; font-weight: 700;">
          🗳️ ${currentTab === 'ballot' ? 'Submit Ballot' : 'Go to Ballot'} &rarr;
        </button>
      </div>
    </aside>
  `;
}
